import { IconAlertCircle, IconShieldQuestion } from '@tabler/icons-react-native'
import { useCallback, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileVerificationButton_profile$key } from '@/__generated__/ProfileVerificationButton_profile.graphql'
import { HStack, Icon, ProfileButton, Toast, ToastTitle, useToast, type ProfileButtonProps } from '@/components'
import { useVerificationRequestMutation } from '@/hooks'

export interface ProfileVerificationButtonProps extends Omit<ProfileButtonProps, 'label' | 'onPress'> {
  profileRef: ProfileVerificationButton_profile$key | null | undefined
}

export const ProfileVerificationButton: FC<ProfileVerificationButtonProps> = ({
  isDisabled: _isDisabled,
  profileRef,
  ...props
}) => {
  const toast = useToast()
  const profileData = useFragment(
    graphql`
      fragment ProfileVerificationButton_profile on Profile {
        isVerified
        name
        userId
        verificationRequested
      }
    `,
    profileRef
  )
  const [createVerificationRequest, isCreatingVerificationRequest] = useVerificationRequestMutation()
  const showToastError = useCallback(
    (message: string) =>
      toast.show({
        placement: 'bottom',
        render: () => (
          <Toast
            action='warning'
            variant='solid'
          >
            <HStack className='items-start gap-2'>
              <Icon
                className='color-black'
                as={IconAlertCircle}
                size='xl'
              />
              <ToastTitle>{message}</ToastTitle>
            </HStack>
          </Toast>
        )
      }),
    [toast]
  )
  const onVerifyPress = useCallback(async () => {
    const response = await createVerificationRequest(profileData?.userId)

    if (response.createVerificationRequest) {
      toast.show({
        placement: 'top',
        render: () => (
          <Toast
            action='success'
            variant='solid'
          >
            <HStack className='items-start gap-2'>
              <Icon
                className='color-black'
                as={IconAlertCircle}
                size='xl'
              />
              <ToastTitle>Verification request sent!</ToastTitle>
            </HStack>
          </Toast>
        )
      })
    } else {
      showToastError('Error sending verification request!')
    }
  }, [createVerificationRequest, profileData?.userId, showToastError, toast])

  const isDisabled = profileData?.verificationRequested ?? (isCreatingVerificationRequest || _isDisabled)

  return !profileData?.isVerified ? (
    <ProfileButton
      accessibilityLabel={
        profileData?.verificationRequested
          ? `Verification requested for ${profileData?.name ? `${profileData.name}'s` : 'this'} profile`
          : `Request verification for ${profileData?.name ? `${profileData.name}'s` : 'this'} profile`
      }
      isDisabled={isDisabled}
      Icon={IconShieldQuestion}
      label={profileData?.verificationRequested ? 'Verification Pending' : 'Request Verification'}
      onPress={onVerifyPress}
      {...props}
    />
  ) : null
}
